import { LEARN_LESSONS } from '@/constants/learnCurriculum';
// Single source of truth for all documentation pages.
// Both the sidebar and search derive their data from this config.
import { pages as businessPages } from '@/content/businessGuides.json';

export const docsNavigation = [
  {
    title: 'Getting Started',
    items: [
      {
        slug: 'index',
        label: 'Introduction',
        icon: 'book-open',
        description: 'Learn what Gab AI is and how it can help you',
        keywords: ['intro', 'welcome', 'start', 'begin', 'overview'],
      },
      {
        slug: 'quickstart',
        label: 'Quickstart',
        icon: 'rocket',
        description: 'Get up and running with Gab AI in minutes',
        keywords: ['quick', 'fast', 'setup', 'guide', 'tutorial'],
      },
    ],
  },
  {
    title: 'Work',
    items: [
      { slug: 'work', label: 'Work overview', icon: 'briefcase', description: 'Start a project, follow progress, and get finished files', keywords: ['work', 'agent', 'computer', 'project', 'start', 'stop', 'credits'] },
      { slug: 'work-prompts', label: 'Writing a good task', icon: 'pen', description: 'Brief Work, attach references, and request revisions', keywords: ['work', 'prompt', 'brief', 'instructions', 'revise'] },
      { slug: 'work-videos', label: 'Making AI videos', icon: 'video', description: 'Produce and refine videos with music and voiceover', keywords: ['work', 'video', 'film', 'music', 'voiceover', 'scenes'] },
      { slug: 'work-files', label: 'Files, code & games', icon: 'file', description: 'Research, create editable files, and build websites and games', keywords: ['work', 'files', 'research', 'documents', 'slides', 'spreadsheets', 'code', 'games', 'html'] },
      { slug: 'work-artifacts', label: 'Artifact editors & exports', icon: 'layer-group', description: 'Choose an artifact and learn its editing and export controls', keywords: ['work', 'artifact', 'editor'] },
      { slug: 'work-games', label: 'Build a video game', icon: 'gamepad', description: 'Create, play, revise, and download an HTML game', keywords: ['work', 'artifact', 'game'] },
      { slug: 'sites', label: 'Publish and share sites', icon: 'globe', description: 'Share websites and games with a public link and manage My sites', keywords: ['work', 'sites', 'my sites', 'publish', 'hosting', 'share', 'public link', 'games', 'html', 'expiration', 'unpublish'] },
      { slug: 'work-images', label: 'Image editor workshop', icon: 'image', description: 'Select a region, refine an image, and download a new version', keywords: ['work', 'artifact', 'image'] },
      { slug: 'work-documents', label: 'Document workshop', icon: 'file-lines', description: 'Create an editable document and export Word or PDF', keywords: ['work', 'artifact', 'document'] },
      { slug: 'work-spreadsheets', label: 'Spreadsheet workshop', icon: 'table', description: 'Build a budget, test formulas, and export Excel', keywords: ['work', 'artifact', 'spreadsheet'] },
      { slug: 'work-presentations', label: 'Presentation workshop', icon: 'chalkboard', description: 'Edit slides and notes and export PowerPoint or PDF', keywords: ['work', 'artifact', 'presentation'] },
    ],
  },
  {
    title: 'Apps & Remote',
    items: [
      { slug: 'mac-app', label: 'Mac app', icon: 'desktop', description: 'Install and set up Gab AI for Mac, learn its features, and keep it updated', keywords: ['mac', 'macos', 'desktop', 'native app', 'download', 'install', 'setup', 'updates', 'keyboard shortcuts', 'dictation', 'voice', 'features'] },
      { slug: 'mac-browser-access', label: 'Browser & app access', icon: 'globe', description: 'Connect browser tabs, install the extension, and manage website and Mac app permissions', keywords: ['mac', 'macos', 'browser', 'brave', 'chrome', 'safari', 'extension', 'side chat', 'permissions', 'accessibility', 'screen recording', 'take over', 'websites'] },
      { slug: 'project-folders', label: 'Project folders', icon: 'folder', description: 'Connect Windows or Mac folders, manage file access, and review or recover changes', keywords: ['windows', 'pc', 'mac', 'project folder', 'local files', 'read-only', 'permissions', 'collections', 'git', 'changes', 'restore', 'recovery', 'disconnect'] },
      { slug: 'mac-local-work', label: 'Local coding & commands', icon: 'terminal', description: 'Use local code and Git with Windows or Mac terminal, command and permission controls', keywords: ['windows', 'pc', 'powershell', 'mac', 'code', 'coding', 'git', 'branch', 'tests', 'builds', 'commands', 'development servers', 'ports', 'environment', 'login shell', 'PATH', 'worktrees'] },
      { slug: 'remote', label: 'Remote setup', icon: 'mobile-screen', description: 'Pair iOS or Android with your Windows PC or Mac and work in shared project folders', keywords: ['remote', 'windows', 'pc', 'mac', 'native apps', 'ios', 'iphone', 'android', 'phone', 'pairing', 'qr code', 'verification code', 'offline', 'shared folders'] },
      { slug: 'windows-app', label: 'Windows app', icon: 'desktop', description: 'Install Gab AI from the Microsoft Store and get started on Windows', keywords: ['windows', 'pc', 'microsoft store', 'desktop', 'native app', 'download', 'install', 'setup', 'updates'] },
    ],
  },
  {
    title: 'Core Concepts',
    items: [
      {
        slug: 'overview',
        label: 'Overview',
        icon: 'circle-info',
        description: 'Understand how the platform works',
        keywords: ['platform', 'how', 'works', 'architecture'],
      },
      {
        slug: 'models',
        label: 'AI Models',
        icon: 'microchip',
        description: 'Explore available models like GPT-5, Claude, Gemini, and Arya',
        keywords: ['gpt', 'claude', 'gemini', 'arya', 'llm', 'language model', 'deepseek'],
      },
      {
        slug: 'credits',
        label: 'Credits System',
        icon: 'coins',
        description: 'Learn how credits work and what they cost',
        keywords: ['credits', 'pricing', 'cost', 'tokens', 'usage', 'billing'],
      },
      {
        slug: 'pricing-guide',
        label: 'Pricing',
        icon: 'tag',
        description: 'Plans, credits, and enterprise options',
        keywords: ['price', 'plan', 'subscription', 'cost', 'enterprise'],
      },
    ],
  },
  {
    title: 'Gab AI Business',
    items: businessPages.map(page => ({ slug: page.slug, label: page.title, icon: page.navIcon, description: page.description, keywords: page.searchKeywords })),
  },
  {
    title: 'Developer',
    items: [
      {
        slug: 'api',
        label: 'API Overview',
        icon: 'code',
        description: 'Introduction to the Gab AI API router',
        keywords: ['api', 'developer', 'integration', 'code', 'vision', 'multimodal', 'image upload', 'file upload', 'auto', 'router', 'openrouter'],
      },
      {
        slug: 'api-auth',
        label: 'Authentication',
        icon: 'key',
        description: 'API keys and authentication flow',
        keywords: ['auth', 'api key', 'token', 'authenticate'],
      },
      {
        slug: 'api-endpoints',
        label: 'Endpoints',
        icon: 'server',
        description: 'Available API endpoints with examples',
        keywords: ['endpoint', 'rest', 'http', 'request', 'embedding', 'embeddings', 'files', 'file upload', 'image upload', 'vision', 'multimodal', 'image_url', 'file_id', 'image edit', 'reference image', 'flux-2-pro', 'images/edit', 'delete image', 'files library'],
      },
      {
        slug: 'claude-code',
        label: 'Claude Code',
        icon: 'terminal',
        description: 'Use Gab AI with Claude Code via the Anthropic-compatible Messages API',
        keywords: ['claude code', 'anthropic', 'cli', 'coding', 'messages api', 'tool use'],
      },
      {
        slug: 'codex-cli',
        label: 'Codex CLI',
        icon: 'code',
        description: 'Use Gab AI with Codex and OpenAI Responses API clients',
        keywords: ['codex', 'openai', 'responses api', 'cli', 'coding', 'function calling'],
      },
      {
        slug: 'pi-cli',
        label: 'Pi CLI',
        icon: 'terminal',
        description: 'Install the official Gab AI package for Pi models and tools',
        keywords: ['pi', 'pi cli', 'pi package', 'gab ai pi', 'npm', 'tool calling', 'coding'],
      },
      {
        slug: 'opencode',
        label: 'OpenCode',
        icon: 'plug',
        description: 'Use Gab AI with OpenCode and OpenAI-compatible coding agents',
        keywords: ['opencode', 'open code', 'openai compatible', 'cli', 'coding', 'tool calling'],
      },
      {
        slug: 'cursor-ide',
        label: 'Cursor IDE',
        icon: 'terminal',
        description: 'Use Gab AI with Cursor IDE',
        keywords: ['cursor', 'ide', 'editor', 'vscode', 'coding'],
      },
      {
        slug: 'openclaw',
        label: 'OpenClaw',
        icon: 'plug',
        description: 'Open source AI integration framework',
        keywords: ['openclaw', 'open source', 'framework', 'integration'],
      },
      {
        slug: 'hermes',
        label: 'Hermes',
        icon: 'terminal',
        description: 'Use Gab AI with Hermes Agent by Nous Research',
        keywords: ['hermes', 'hermes agent', 'nous research', 'nous', 'custom endpoint', 'openai compatible', 'agent'],
      },
      {
        slug: 'prime-agent',
        label: 'Prime Agent',
        icon: 'terminal',
        description: 'Use Gab AI with Prime Agent by Prime Intellect',
        keywords: ['prime agent', 'prime-agent', 'prime intellect', 'rlm', 'custom provider', 'openai compatible', 'coding agent'],
      },
    ],
  },
  {
    title: 'Features',
    items: [
      { slug: 'maps', label: 'Maps & Place Guides', icon: 'map', description: 'Find places, read sourced guides, and plan an outing', keywords: ['maps', 'places', 'location', 'hours', 'itinerary', 'outing'] },
      { slug: 'interactive-cards', label: 'Interactive Answer Cards', icon: 'list-check', description: 'Use plans, checklists, comparisons, and study cards in Chat', keywords: ['cards', 'checklist', 'meal plan', 'itinerary', 'comparison', 'study', 'progress'] },
      { slug: 'plugins', label: 'Plugins & Connected Accounts', icon: 'puzzle-piece', description: 'Install skills and connect supported services or MCP servers on Mac', keywords: ['plugins', 'skills', 'connections', 'mcp', 'oauth', 'mac'] },
      { slug: 'custom-instructions', label: 'Custom Instructions for Chat & Work', icon: 'pen', description: 'Save separate Chat preferences and Work agent rules', keywords: ['custom instructions', 'instructions', 'agent rules', 'work rules', 'user defined rules', 'personalization', 'coding style', 'tests', 'specs'] },
      {
        slug: 'chat',
        label: 'Chat',
        icon: 'message',
        description: 'Main chat interface and conversation management',
        keywords: ['chat', 'conversation', 'message', 'talk'],
      },
      {
        slug: 'tools',
        label: 'AI Tools',
        icon: 'toolbox',
        description: 'Purpose-built AI workflows for creation, editing, writing, analysis, code, and business',
        keywords: ['tools', 'tool', 'workflow', 'studio', 'create', 'edit', 'write', 'analyze', 'code', 'business'],
      },
      {
        slug: 'images',
        label: 'Image Generation',
        icon: 'image',
        description: 'Create AI-generated images with various styles',
        keywords: ['image', 'picture', 'generate', 'art', 'midjourney', 'gpt image', 'flux'],
      },
      {
        slug: 'videos',
        label: 'Video Generation',
        icon: 'video',
        description: 'Create AI-generated videos',
        keywords: ['video', 'movie', 'clip', 'animate'],
      },
      {
        slug: 'music',
        label: 'Music Generation',
        icon: 'music',
        description: 'Generate music and songs with AI',
        keywords: ['music', 'song', 'audio', 'lyrics', 'suno'],
      },
      {
        slug: 'voice',
        label: 'Voice Mode',
        icon: 'microphone',
        description: 'Real-time voice conversations with AI',
        keywords: ['voice', 'speak', 'talk', 'speech', 'audio', 'microphone'],
      },
      {
        slug: 'agents',
        label: 'Bots',
        icon: 'robot',
        description: 'Create personalized AI assistants',
        keywords: ['bots', 'bot', 'custom', 'assistant', 'instructions', 'favorites', 'agents'],
      },
      {
        slug: 'memory',
        label: 'AI Memory',
        icon: 'brain',
        description: 'Personalized memory across conversations',
        keywords: ['memory', 'remember', 'personalize', 'context'],
      },
      {
        slug: 'collections',
        label: 'Collections',
        icon: 'folder',
        description: 'Organize chats and files into groups',
        keywords: ['collection', 'organize', 'folder', 'group'],
      },
      {
        slug: 'bookmarks',
        label: 'Bookmarks',
        icon: 'bookmark',
        description: 'Save important AI messages and organize them into folders',
        keywords: ['bookmark', 'save', 'favorite', 'starred', 'pin', 'organize'],
      },
      {
        slug: 'files',
        label: 'Files',
        icon: 'file',
        description: 'Upload and chat with documents',
        keywords: ['file', 'document', 'upload', 'pdf', 'attachment'],
      },
      {
        slug: 'editors',
        label: 'Docs, Slides & Sheets',
        icon: 'pen-to-square',
        description: 'Create and edit documents, presentations, and spreadsheets with AI',
        keywords: ['editor', 'document', 'presentation', 'spreadsheet', 'powerpoint', 'excel', 'word', 'pdf', 'slides', 'deck', 'docx', 'pptx', 'xlsx'],
      },
      {
        slug: 'web-search',
        label: 'Web Search',
        icon: 'globe',
        description: 'Real-time web search integration',
        keywords: ['search', 'web', 'internet', 'browse', 'google'],
      },
      {
        slug: 'tasks',
        label: 'Automated Tasks',
        icon: 'clock',
        description: 'Schedule and automate AI tasks',
        keywords: ['task', 'automate', 'schedule', 'recurring', 'cron'],
      },
      {
        slug: 'import-chats',
        label: 'Import Chats',
        icon: 'file-import',
        description: 'Import conversations from other AI platforms',
        keywords: ['import', 'migrate', 'chatgpt', 'transfer', 'export'],
      },
      {
        slug: 'data-export',
        label: 'Download Your Data',
        icon: 'box-archive',
        description: 'Export a portable ZIP archive of your account',
        keywords: ['data export', 'download data', 'account export', 'archive', 'gdpr', 'ccpa', 'portability', 'backup'],
      },
    ],
  },
  {
    title: 'Reference',
    items: [
      {
        slug: 'privacy',
        label: 'Privacy & Values',
        icon: 'shield-halved',
        description: 'Privacy protection and company values',
        keywords: ['privacy', 'values', 'christian', 'security', 'data'],
      },
    ],
  },
];

export const getDocHref = (slug) =>
  `/docs/${slug}`;

export const getAllDocsPages = () => {
  const pages = [];
  for (const section of docsNavigation) {
    for (const item of section.items) {
      pages.push({
        ...item,
        href: getDocHref(item.slug),
        category: section.title,
      });
    }
  }
  return pages;
};

export const getSearchableContent = () => [...getAllDocsPages(), ...LEARN_LESSONS.map(lesson => ({ ...lesson, description: `AI lesson · ${lesson.minutes} min`, keywords: ['learn', 'lesson', lesson.category], category: `Learn · ${lesson.category}` }))];

export const getDocsPageNav = (currentPathname) => {
  const pages = getAllDocsPages();
  const normalizedPath =
    currentPathname === '/docs' ? '/docs' : currentPathname;
  const index = pages.findIndex(
    (p) =>
      p.href === normalizedPath ||
      (p.slug === 'index' && normalizedPath === '/docs'),
  );
  if (index === -1) return { previous: null, next: null };
  return {
    previous:
      index > 0
        ? { label: pages[index - 1].label, href: pages[index - 1].href }
        : null,
    next:
      index < pages.length - 1
        ? { label: pages[index + 1].label, href: pages[index + 1].href }
        : null,
  };
};

export const getDocsPageInfo = (pathname) => {
  for (const section of docsNavigation) {
    for (const item of section.items) {
      const itemPath = getDocHref(item.slug);
      if (
        pathname === itemPath ||
        (item.slug === 'index' && pathname === '/docs')
      ) {
        return { section: section.title, page: item.label, ...item };
      }
    }
  }
  return { section: 'Docs', page: 'Documentation' };
};
